import { AllJointSystemReadModel, AllMarginalContributorReadModel, LabCaseRunPrintOptionsModel, ResultHypothesisReadModel, TopMarginalSystemReadModel } from '@app/api2/api';
import { BPTabPane } from '@app/components/React/Common/BPTabPane';
import { CleanTable } from '@app/components/React/Common/CleanTable';
import { PageBreak } from '@app/components/React/Common/PageBreak';
import * as React from 'react';
import { Divider, Grid, GridColumn, GridRow, Message, MessageHeader, Tab, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from '@/components/bp/bp-ui';
import { BPBoxWithHeader } from '@common/BPBoxWithHeader';
import { BPPlotly } from '@common/BPPlotly';
import { InfoBox } from '@common/InfoBox';
import { anyToDecimal, isNullOrWhitespace, replaceInString, toSci, toSciDecimal } from '@common/Utils';
import { Deconvolution } from "./Deconvolution";
import Decimal from 'decimal.js';

interface IHypothesisProps {
    model: ResultHypothesisReadModel;
    highlightAbove?: number;
    epg?: {
        pngUrl: string;
        plotlyUrl: string;
    };
    forPrint?: LabCaseRunPrintOptionsModel;
}


function ParameterEstimates(props) {
    return (<Table as={props.forPrint ? CleanTable : Table} definition striped compact>
        <TableHeader>
            <TableRow>
                <TableHeaderCell>{window.translation.common.parameter}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.mle}</TableHeaderCell>
                <TableHeaderCell>{window.translation.caseRunView.stdError}</TableHeaderCell>
            </TableRow>
        </TableHeader>
        <TableBody>
            {props.model?.mixProps.sort((a, b) => a.number - b.number).map(mp => <TableRow key={`${props.model.type}_mx_${mp.mixProp}`}>
                <TableCell><span dangerouslySetInnerHTML={{
                    __html: replaceInString(window.translation.caseRunView.mixPropNumber, {
                        '{mixPropNumber}': mp.number,
                        '{sampleName}': mp.sampleName
                    })
                }}></span></TableCell>
                <TableCell>{toSci(mp.mixProp, 5)}</TableCell>
                <TableCell>{toSci(mp.mixPropError, 5)}</TableCell>
            </TableRow>)}
            <TableRow>
                <TableCell>{window.translation.caseRunView.phExpectation}</TableCell>
                <TableCell>{toSci(props.model.phExpectation, 5)}</TableCell>
                <TableCell>{toSci(props.model.phExpectationError, 5)}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell>{window.translation.caseRunView.phVariability}</TableCell>
                <TableCell>{toSci(props.model.phVariability, 5)}</TableCell>
                <TableCell>{toSci(props.model.phVariabilityError, 5)}</TableCell>
            </TableRow>
            {props.model?.degradationSlope != 0 && <tr>
                <td>{window.translation.caseRunView.degradationSlope}</td>
                <td>{toSci(props.model?.degradationSlope)}</td>
                <td>{toSci(props.model?.degradationSlopeError)}</td>
            </tr>}
            {props.model?.stutterProp != 0 && <tr>
                <td>{window.translation.caseRunView.stutterProp}</td>
                <td>{toSci(props.model?.stutterProp)}</td>
                <td>{toSci(props.model?.stutterPropError)}</td>
            </tr>}
            {props.model?.fwStutterProp != 0 && <tr>
                <td>{window.translation.caseRunView.fwStutterProp}</td>
                <td>{toSci(props.model?.fwStutterProp)}</td>
                <td>{toSci(props.model?.fwStutterPropError)}</td>
            </tr>}
            {props.model?.acceptanceRate != 0 && <tr>
                <td>{window.translation.caseRunView.samplingAcceptanceRate}</td>
                <td>{toSci(props.model?.acceptanceRate)}</td>
                <td></td>
            </tr>}
        </TableBody>
    </Table>);
}



function MaximumLikelihood(props: IHypothesisProps) {
    return (<BPBoxWithHeader forPrint={props.forPrint != null} header={window.translation.caseRunView.maximumLikelihood}>
        <InfoBox compact items={[{
            width: 10,
            label: window.translation.caseRunView.logLikelihood,
            description: toSci(props.model.logLikelihood)
        }, {
            width: 10,
            label: window.translation.caseRunView.likelihood,
            description: toSciDecimal(anyToDecimal(props.model.likelihoodStr))
        }]}></InfoBox>
    </BPBoxWithHeader>);
}



interface MaxLrDeconvolutionProps {
    forPrint?: LabCaseRunPrintOptionsModel;
    model: ResultHypothesisReadModel;
    highlightAbove: number;
}

function MaxLrDeconvolution(props: MaxLrDeconvolutionProps) {
    const deconvAllJoint = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvAllJoint : props.forPrint.hdDeconvAllJoint : true;
    const deconvAllMarginalAlleles = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvAllMarginalAlleles : props.forPrint.hdDeconvAllMarginalAlleles : true;
    const deconvAllMarginalGeno = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvAllMarginalGeno : props.forPrint.hdDeconvAllMarginalGeno : true;
    const deconvTopMarginal = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpDeconvTopMarginal : props.forPrint.hdDeconvTopMarginal : true;

    return deconvAllMarginalAlleles || deconvAllMarginalGeno || deconvTopMarginal || deconvAllJoint ? (<BPBoxWithHeader forPrint={props.forPrint != null} header={window.translation.caseRunView.deconvolution}>
        <Deconvolution forPrint={props.forPrint} model={props.model} highlightAbove={props.highlightAbove} />
    </BPBoxWithHeader>) : <></>;
}


export function Hypothesis(props: IHypothesisProps) {
    const forPrint = props.forPrint != null;

    const parameterEstimates = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpParameterEstimates : props.forPrint.hdParameterEstimates : true;
    const mcmcParameters = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpMcmcParameters : props.forPrint.hdMcmcParameters : true;
    const modelValidation = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpModelValidation : props.forPrint.hdModelValidation : true;
    const modelFitted = props.forPrint ? props.model.type == 'p' ? props.forPrint.hpFittedModel : props.forPrint.hdFittedModel : true;

    if (props.model == null)
        return null;

    const graphPanes = [
        ...!isNullOrWhitespace(props.model.mleUrl) && modelValidation ? [{ menuItem: window.translation.caseRunView.modelValidation, pane: <BPTabPane key={`${props.model.type}_mlePane`}><img className="svgcontainer" src={props.model.mleUrl} /></BPTabPane> }] : [],
        ...(!isNullOrWhitespace(props.model.fittedEpgUrl) || !isNullOrWhitespace(props.model.fittedEpgPlotlyUrl)) && modelFitted ? [{
            menuItem: window.translation.caseRunView.fittedModel, pane: <BPTabPane key={`${props.model.type}_fittedPane`}>
                {!isNullOrWhitespace(props.model.fittedEpgPlotlyUrl) && !props.forPrint && <>
                    <BPPlotly width={900} jsonUrl={props.model.fittedEpgPlotlyUrl} title={window.translation.caseRunView.fittedModel} />
                    <Divider />
                </>}
                <img className="svgcontainer" src={props.model.fittedEpgUrl} />
            </BPTabPane>
        }] : [],
        ...!isNullOrWhitespace(props.model.mcmcSvg) && mcmcParameters ? [{ menuItem: window.translation.caseRunView.mcmcParameters, pane: <BPTabPane key={`${props.model.type}_mcmcPane`}><img className="svgcontainer" src={props.model.mcmcSvg} /></BPTabPane> }] : [],
        ...props.epg && (!forPrint || props.forPrint.resultsEpgOverlay) ? [{
            menuItem: window.translation.caseRunView.epgOverlay, pane: <BPTabPane key={`${props.model.type}_epgPane`}>
                {!isNullOrWhitespace(props.epg?.plotlyUrl) && <>
                    <BPPlotly width={forPrint ? null : 900} autoWidth={forPrint} jsonUrl={props.epg?.plotlyUrl} title={window.translation.caseRunView.epgOverlay} />
                    {/* <Divider /> */}
                </>}
                {/* <img className="svgcontainer" src={props.epg.pngUrl} /> */}
            </BPTabPane>
        }] : [],
    ];

    return !props.forPrint ? (
        <Grid>
            {props.model.logLikelihood == null && <GridRow>
                <GridColumn width={16}>
                    <Message error>
                        <MessageHeader>Error in model</MessageHeader>
                        <p>
                            Please confirm that the POI is visually excluded under the current model.
                        </p></Message>
                    {/* <BPBoxWithHeader forPrint={forPrint}>
                        asddasdfsaf
                    </BPBoxWithHeader> */}
                </GridColumn>
            </GridRow>}

            <GridRow>
                <GridColumn width={8}>
                    <BPBoxWithHeader forPrint={forPrint} header={window.translation.caseRunView.parameterEstimates}>
                        <ParameterEstimates forPrint={props.forPrint} model={props.model}></ParameterEstimates>
                    </BPBoxWithHeader>
                </GridColumn>
                <GridColumn width={8}>
                    <MaximumLikelihood forPrint={props.forPrint} model={props.model}></MaximumLikelihood>
                </GridColumn>
            </GridRow>
            <GridRow>
                <GridColumn width={16}>
                    <BPBoxWithHeader forPrint={forPrint} header={window.translation.caseRunView.graphs}>
                        {graphPanes.length > 0 &&
                            <Tab
                                renderActiveOnly={false}
                                panes={graphPanes}
                                menu={{ secondary: true, pointing: true }} />}
                        {graphPanes.length == 0 &&
                            <div>{window.translation.caseRunView.noGraphs}</div>}
                    </BPBoxWithHeader>
                </GridColumn>
            </GridRow>
            <GridRow>
                <GridColumn>
                    <MaxLrDeconvolution forPrint={props.forPrint} model={props.model} highlightAbove={props.highlightAbove}></MaxLrDeconvolution>
                </GridColumn>
            </GridRow>
        </Grid>
    ) : <>
        {parameterEstimates &&
            <table className={'two-col-layout'}>
                <tbody>
                    <tr>
                        <td>
                            <ParameterEstimates forPrint={forPrint} model={props.model}></ParameterEstimates>
                        </td>
                        <td>
                            <MaximumLikelihood forPrint={props.forPrint} model={props.model}></MaximumLikelihood>
                        </td>
                    </tr>
                </tbody>
            </table>

        }
        {graphPanes.map(x => (<React.Fragment key={x.menuItem}>
            <PageBreak />
            <BPBoxWithHeader key={x.menuItem} forPrint={forPrint} header={x.menuItem}>
                {x.pane}
            </BPBoxWithHeader>
        </React.Fragment>))}
        <PageBreak />
        <MaxLrDeconvolution forPrint={props.forPrint} model={props.model} highlightAbove={props.highlightAbove}></MaxLrDeconvolution>
    </>;
}

